import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ShellAtnaTurn, ShellTurn } from '../types/atna'

export type EauKind = 'consommation' | 'fuite' | 'conseils' | 'alerte' | 'facture' | 'suivi'

const THINKING_MS = 800

/** Associe la question du citoyen au bon volet du module Compteur d'eau. */
export function detectEauKind(text: string): EauKind {
  const lower = text.toLowerCase()
  if (/fuite|fuit|coule|goutte|débit de nuit|debit de nuit/.test(lower)) return 'fuite'
  if (/réduire|reduire|économis|economis|conseil|astuce|moins d’eau|moins d'eau/.test(lower)) return 'conseils'
  if (/alerte|avertir|avis|notif|seuil|prévenir|prevenir/.test(lower)) return 'alerte'
  if (/payer|facture|montant|coût|cout|solde|paiement/.test(lower)) return 'facture'
  return 'consommation'
}

/**
 * Flux ATNA du module Compteur d'eau : lecture de la consommation, détection de
 * fuite, conseils d'économie, alertes et paiement de la facture.
 */
export function useEauConversation(initialText: string) {
  const seed = initialText.trim()

  const [turns, setTurns] = useState<ShellTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback((kind: EauKind) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setThinking(true)
    timerRef.current = window.setTimeout(() => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
      setThinking(false)
    }, THINKING_MS)
  }, [])

  const exchange = useCallback(
    (userText: string, kind: EauKind) => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'user', text: userText }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    reply(detectEauKind(seed))
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is ShellAtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')
    exchange(text, detectEauKind(text))
  }, [draft, exchange, thinking])

  /** Confirme une action déclenchée depuis une bulle ATNA. */
  const acknowledge = useCallback(
    (userText: string) => {
      exchange(userText, 'suivi')
    },
    [exchange],
  )

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    acknowledge,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind as EauKind | undefined,
  }
}
